import Config from '@/config'
import Pusher from 'pusher-js'

const EXPORT_COMPLETE_MODULES = ['exports', 'survey-list-export', 'customer-list-export']

export default {
  namespaced: true,
  state: {
    pusher: null,
    userChannel: null,
    winbackChannel: null,
    winbackConversationsAllCompaniesChannel: null,
    connectionReady: false,
    userChannelReady: false,
    connectionInProgress: false
  },
  getters: {
    selectPusher: (state) => state.pusher,
    selectUserChannel: (state) => state.userChannel,
    selectUserChannelReady: (state) => state.userChannelReady,
    selectWinbackChannel: (state) => state.winbackChannel,
    selectWinbackConversationsAllCompaniesChannel: (state) =>
      state.winbackConversationsAllCompaniesChannel,
    selectConnectionReady: (state) => state.connectionReady,
    selectConnectionInProgress: (state) => state.connectionInProgress
  },
  mutations: {
    SET_PUSHER(state, pusher) {
      state.pusher = pusher
    },
    SET_USER_CHANNEL(state, userChannel) {
      state.userChannel = userChannel
    },
    SET_USER_CHANNEL_READY(state, status) {
      state.userChannelReady = status
    },
    SET_WINBACK_CHANNEL(state, winbackChannel) {
      state.winbackChannel = winbackChannel
    },
    SET_WINBACK_CONVERSATIONS_ALL_COMPANIES_CHANNEL(state, channel) {
      state.winbackConversationsAllCompaniesChannel = channel
    },
    SET_CONNECTION_READY(state, status) {
      state.connectionReady = status
    },
    SET_CONNECTION_IN_PROGRESS(state, status) {
      state.connectionInProgress = status
    }
  },
  actions: {
    async instantiatePusher(_, { apiApp, pusherKey }) {
      return new Pusher(pusherKey, {
        cluster: 'mt1',
        forceTLS: true,
        activityTimeout: 30000, // sends a ping message after 30 seconds of no messages
        pongTimeout: 10000, // response to ping if it takes longer than 10 seconds assumes dead
        // TODO: set up to support multiple environments
        channelAuthorization: {
          headers: {
            authorization: localStorage.getItem('jwt')
          },
          endpoint: `${apiApp}/app-services/v2/websocket-notification/channel/authorization`
        },
        userAuthentication: {
          headers: {
            authorization: localStorage.getItem('jwt')
          },
          endpoint: `${apiApp}/app-services/v2/websocket-notification/user/authorization`
        }
      })
    },

    async pusherConnect({ commit, dispatch, getters, rootGetters }) {
      if (getters.selectPusher || !localStorage.getItem('jwt') || !rootGetters['user/onlineUser']) {
        console.warn('Pusher connection skipped: missing requirements or already connected')
        return Promise.resolve()
      }

      return new Promise(async (resolve, reject) => {
        if (getters.selectConnectionInProgress) {
          return Promise.resolve()
        } else {
          commit('SET_CONNECTION_IN_PROGRESS', true)
        }
        const { apiApp, pusherKey } = Config

        let pusher
        try {
          pusher = await dispatch('instantiatePusher', { apiApp, pusherKey })
        } catch (error) {
          console.log('error', error)
          if (!pusher?.sessionID) {
            await new Promise((resolve) => setTimeout(resolve, 2000)) // wait 2 seconds
            console.log('no pusher instance, retrying...')
            pusher = await dispatch('instantiatePusher', { apiApp, pusherKey })
          } else {
            return
          }
        }

        pusher.signin()
        commit('SET_PUSHER', pusher)

        if (getters.selectPusher?.sessionID) {
          try {
            // implement this when other channels are built out
            pusher.connection.bind('connected', async () => {
              commit('SET_CONNECTION_READY', true)
              await dispatch('userChannelSubscribe')
              resolve()
            })

            pusher.connection.bind('disconnected', async () => {
              commit('SET_PUSHER', null)
              commit('SET_CONNECTION_READY', false)
              commit('SET_USER_CHANNEL_READY', false)
              setTimeout(async () => {
                await dispatch('pusherConnect', 'disconnected')
              }, 3000)
            })

            pusher.connection.bind('failed', async () => {
              commit('SET_PUSHER', null)
              commit('SET_CONNECTION_READY', false)
              commit('SET_USER_CHANNEL_READY', false)
            })
          } catch (error) {
            console.log('error', error)
          }
          commit('SET_CONNECTION_IN_PROGRESS', false)
        }
      })
    },

    async pusherDisconnect({ commit, dispatch, getters }) {
      dispatch('userChannelUnsubscribe')
      if (getters.selectWinbackChannel) {
        dispatch('winbackCompanyChannelUnsubscribe')
      }
      const pusher = getters.selectPusher
      if (!pusher) {
        console.log('no pusher in pusherDisconnect')
        return
      }

      // TODO: implement these when company/conversation channels are built out
      // dispatch('companyChannelUnsubscribe')
      // dispatch('conversationChannelUnsubscribe')

      pusher.disconnect()
      commit('SET_PUSHER', null)
    },

    async userChannelSubscribe({ getters, rootGetters, dispatch, commit }) {
      return new Promise((resolve, reject) => {
        const pusher = getters.selectPusher
        if (!pusher) {
          console.log('no pusher in userChannelSubscribe')
          return
        }
        const channel = pusher.subscribe(`private-user-${rootGetters['user/onlineUser']._id}`)

        channel.bind('pusher:subscription_error', (error) => {
          console.log(error)
          reject(error)
        })
        channel.bind('pusher:subscription_succeeded', (val) => {
          commit('SET_USER_CHANNEL_READY', true)
          resolve()
        })
        channel.bind('CONVERSATION_UPDATED', (value) => {
          dispatch('conversationV2/processConversationUpdated', value.conversation, { root: true })
        })
        channel.bind('CONVERSATION_ADDED', (value) => {
          dispatch('conversationV2/processConversationAdded', value.conversation, { root: true })
        })
        channel.bind('WINBACK_CONVERSATION_UPDATED', (value) => {
          dispatch('conversationV2/processConversationUpdated', value.payload?.conversation, {
            root: true
          })
        })
        channel.bind('WINBACK_CONVERSATION_ADDED', (value) => {
          dispatch('conversationV2/processConversationAdded', value.payload?.conversation, {
            root: true
          })
        })
        channel.bind('TIMELINE_EVENT_ADDED', (value) => {
          dispatch('timelineV2/processTimelineEventAdded', value.analyticEvent, { root: true })
        })
        channel.bind('PREVIEW_CUSTOM_FILTERS', (value) => {
          dispatch('campaignSetups/setCustomFiltersResponse', value, { root: true })
        })
        // The gift-card issue request may never get an HTTP response: API Gateway gives up
        // at 29s while the Shift4 call cycle can run to 60s, so on a slow failure the
        // operator's request has already 504'd by the time the outcome is known. This is
        // the only channel that can tell them. Ephemeral by design — nothing is persisted
        // and nothing lands in the guest's conversation history.
        channel.bind('GIFT_CARD_ISSUE_FAILED', (value) => {
          dispatch('conversationV2/processGiftCardIssueFailed', value?.data ?? {}, { root: true })
        })
        // The success twin, and not redundant with the HTTP 201: on the same slow path
        // the operator has no response at all, and silence is indistinguishable from
        // failure. Without this they re-stage the card, which mints a new
        // Idempotency-Key and funds a second one.
        channel.bind('GIFT_CARD_ISSUE_SUCCEEDED', (value) => {
          dispatch('conversationV2/processGiftCardIssueSucceeded', value?.data ?? {}, {
            root: true
          })
        })
        channel.bind('EXPORT_COMPLETE', (value) => {
          if (value.module && !EXPORT_COMPLETE_MODULES.includes(value.module)) return
          dispatch('exports/exportComplete', { data: { url: value.url } }, { root: true })
        })
        channel.bind('CUSTOMER_LIST_RECORDS', (value) => {
          dispatch('customer/receiveListRecords', value, { root: true })
        })
        channel.bind('CUSTOMER_LIST_COUNT', (value) => {
          dispatch('customer/receiveListCount', value, { root: true })
        })

        commit('SET_USER_CHANNEL', channel)
      })
    },

    /**
     * Resolves once the user channel has actually subscribed.
     *
     * `connectionReady` flips on the connection's `connected` event, which is before
     * `userChannelSubscribe` has bound anything, so it cannot stand in for this. Pusher does not
     * replay to a client that subscribes after a publish, so anything dispatched between those two
     * points is lost outright. Polls on the same interval and cap as
     * `winbackV2ConversationsAllCompaniesPresenceSubscribe`, but reports the outcome rather than
     * failing silently: the caller has a request to abandon.
     */
    async awaitUserChannelReady({ getters }) {
      const POLL_INTERVAL_MS = 250
      const POLL_MAX_ATTEMPTS = 20 // ~5s total

      let attempts = 0
      while (!getters.selectUserChannelReady && attempts < POLL_MAX_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
        attempts += 1
      }

      return getters.selectUserChannelReady
    },

    async userChannelUnsubscribe({ getters, rootGetters, commit }) {
      const pusher = getters.selectPusher
      if (!pusher) {
        console.log('no pusher in userChannelUnsubscribe')
        return
      }
      commit('SET_USER_CHANNEL_READY', false)
      getters.selectUserChannel.unbind_all()
      pusher.unsubscribe(`private-user-${rootGetters['user/onlineUser']._id}`)
    },

    async resyncWinbackSubscription({ dispatch, getters, rootGetters }) {
      if (!getters.selectConnectionReady) {
        return
      }

      const activeCompany = rootGetters['company/activeCompany']
      const isAllCompanies = !activeCompany?._id || activeCompany._id === 'ALL_COMPANIES'

      await dispatch('winbackCompanyChannelUnsubscribe')

      if (!isAllCompanies) {
        await dispatch('winbackCompanyChannelSubscribe')
      }
    },

    async winbackCompanyChannelSubscribe({ getters, rootGetters, commit, dispatch }) {
      return new Promise((resolve, reject) => {
        const pusher = getters.selectPusher
        if (!pusher) {
          console.log('no pusher in winbackCompanyChannelSubscribe')
          return
        }
        const channel = pusher.subscribe(
          `presence-company-${rootGetters['company/activeCompany']._id}-winback`
        )
        channel.bind('pusher:subscription_error', (error) => {
          console.log(error)
          reject(error)
        })
        channel.bind('pusher:subscription_succeeded', (val) => {
          resolve()
        })

        commit('SET_WINBACK_CHANNEL', channel)
      })
    },

    async winbackCompanyChannelUnsubscribe({ getters, rootGetters }) {
      const pusher = getters.selectPusher
      if (!pusher) {
        console.log('no pusher in winbackCompanyChannelUnsubscribe')
        return
      }
      const channel = getters.selectWinbackChannel
      if (!channel) return
      channel.unbind_all()
      pusher.unsubscribe(channel.name)
    },

    async winbackV2ConversationsAllCompaniesPresenceSubscribe({ getters, commit }) {
      // Pusher may not be instantiated yet when a view mounts and triggers this
      // subscribe. Instead of bailing immediately, recheck for the instance with
      // a short delay between attempts, capped so we never busy-loop forever.
      const PUSHER_POLL_INTERVAL_MS = 250
      const PUSHER_POLL_MAX_ATTEMPTS = 20 // ~5s total

      // Already subscribed — don't double-subscribe on a re-entrant call.
      if (getters.selectWinbackConversationsAllCompaniesChannel) {
        return
      }

      let pusher = getters.selectPusher
      let attempts = 0
      while (!pusher && attempts < PUSHER_POLL_MAX_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, PUSHER_POLL_INTERVAL_MS))
        attempts += 1
        pusher = getters.selectPusher
      }

      if (!pusher) {
        // Cap exhausted and pusher still unavailable — fail soft, matching the
        // other subscribe actions: don't throw, don't subscribe.
        console.log('no pusher in winbackV2ConversationsAllCompaniesPresenceSubscribe')
        return
      }

      return new Promise((resolve, reject) => {
        const channel = pusher.subscribe('presence-winback-conversations-all-companies')
        channel.bind('pusher:subscription_error', (error) => {
          console.log(error)
          reject(error)
        })
        channel.bind('pusher:subscription_succeeded', () => {
          resolve()
        })
        commit('SET_WINBACK_CONVERSATIONS_ALL_COMPANIES_CHANNEL', channel)
      })
    },

    async winbackV2ConversationsAllCompaniesPresenceUnsubscribe({ getters, commit }) {
      const pusher = getters.selectPusher
      if (!pusher) {
        console.log('no pusher in winbackV2ConversationsAllCompaniesPresenceUnsubscribe')
        return
      }
      const channel = getters.selectWinbackConversationsAllCompaniesChannel
      if (channel) {
        channel.unbind_all()
        pusher.unsubscribe(channel.name)
      }
      commit('SET_WINBACK_CONVERSATIONS_ALL_COMPANIES_CHANNEL', null)
    },

    async companyChannelSubscribe({ getters, rootGetters }) {
      return new Promise((resolve, reject) => {
        const pusher = getters.selectPusher
        if (!pusher) {
          console.log('no pusher in companyChannelSubscribe')
          return
        }
        const channel = pusher.subscribe(
          `presence-company-${rootGetters['company/activeCompany']._id}`
        )
        channel.bind('pusher:subscription_error', (error) => {
          console.log(error)
          reject(error)
        })
        channel.bind('pusher:subscription_succeeded', (val) => {
          console.log(val)
          resolve()
        })
      })
    },

    async companyChannelUnsubscribe({ getters, rootGetters }) {
      const pusher = getters.selectPusher
      if (!pusher) {
        console.log('no pusher in companyChannelUnsubscribe')
        return
      }
      pusher.unsubscribe(`presence-company-${rootGetters['company/activeCompany']._id}`)
    },

    async conversationChannelSubscribe({ getters }, { conversationId }) {
      return new Promise((resolve, reject) => {
        const pusher = getters.selectPusher
        if (!pusher) {
          console.log('no pusher in conversationChannelSubscribe')
          resolve()
          return
        }
        const channel = pusher.subscribe(`presence-conversation-${conversationId}`)
        channel.bind('pusher:subscription_error', (error) => {
          console.log(error)
          reject(error)
        })
        channel.bind('pusher:subscription_succeeded', (val) => {
          console.log(val)
          resolve()
        })
      })
    },

    async conversationChannelUnsubscribe({ getters }, { conversationId }) {
      const pusher = getters.selectPusher
      if (!pusher) {
        console.log('no pusher in conversationChannelUnsubscribe')
        return
      }
      const channel = pusher.channel(`presence-conversation-${conversationId}`)
      if (channel) {
        channel.unbind_all()
      }
      pusher.unsubscribe(`presence-conversation-${conversationId}`)
    }
  }
}
